import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Mail, Phone, MapPin, Send, CheckCircle2, Globe, Clock } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Public Contact-Us page.
 * All displayed info (address, email, phone, website, hours) is driven by
 * the Contact-Us CMS (`GET /api/contact-info`) so admins can edit it live
 * from the dashboard. Falls back to defaults if the API blips.
 */
export const ContactPage = () => {
  const { t, language } = useLanguage();
  const { user } = useAuth();
  const isRTL = language === 'ar';
  const [submitted, setSubmitted] = useState(false);
  const [sending, setSending] = useState(false);
  const [content, setContent] = useState(null);
  const [form, setForm] = useState({
    full_name: user?.full_name || '',
    email: user?.email || '',
    phone: user?.phone_number || '',
    subject: '',
    message: '',
  });

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/contact-info`)
      .then((res) => { if (!cancelled) setContent(res.data); })
      .catch(() => { /* Keep null → renders after retry / defaults from server */ });
    return () => { cancelled = true; };
  }, []);

  // AR-fallback picker for bilingual fields.
  const pick = (bilingual) => {
    if (!bilingual) return '';
    const primary = bilingual[language];
    if (primary && primary.trim()) return primary;
    return bilingual.ar || bilingual.en || '';
  };

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (sending) return;
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      setSubmitted(true);
      toast.success(t('تم إرسال رسالتك بنجاح', 'Your message has been sent', 'Message envoyé'));
    } catch (err) {
      const detail = err?.response?.data?.detail;
      let msg;
      if (Array.isArray(detail) && detail[0]?.msg) msg = detail[0].msg;
      else if (typeof detail === 'string') msg = detail;
      else msg = err.message;
      toast.error(`${t('خطأ', 'Error', 'Erreur')}: ${msg}`);
    } finally {
      setSending(false);
    }
  };

  // Render skeleton until content loads (prevents flash of hardcoded copy).
  const header = content?.header || {};
  const info = content?.info || {};

  return (
    <div
      className={`min-h-screen bg-[#f4f7fb] ${isRTL ? 'rtl' : 'ltr'}`}
      dir={isRTL ? 'rtl' : 'ltr'}
      data-testid="contact-page"
    >
      {/* Header band */}
      <div className="bg-gradient-to-b from-[#0d47a1] via-[#1565c0] to-[#1e88e5] text-white py-10 px-4">
        <div className="max-w-3xl mx-auto flex items-center gap-4">
          <div className="w-14 h-14 rounded-full bg-white/15 backdrop-blur flex items-center justify-center flex-shrink-0">
            <Mail size={28} strokeWidth={1.8} />
          </div>
          <div className="flex-1">
            <h1 className="text-2xl md:text-3xl font-extrabold" data-testid="contact-page-title">
              {pick(header.title) || t('تواصل معنا', 'Contact Us', 'Contactez-nous')}
            </h1>
            <p className="text-blue-100 text-sm mt-1">
              {pick(header.subtitle) || t('فريقنا في خدمتك على مدار الساعة', 'Our team is here to help you anytime', 'Notre équipe est à votre service')}
            </p>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-8 grid md:grid-cols-3 gap-6">
        {/* Info column — fully driven by /api/contact-info */}
        <div className="md:col-span-1 space-y-4">
          {pick(info.address) && (
            <Card className="p-5" data-testid="contact-info-address">
              <div className="flex items-start gap-3">
                <span className="w-10 h-10 rounded-xl bg-blue-50 text-[#1565c0] flex items-center justify-center flex-shrink-0">
                  <MapPin size={18} />
                </span>
                <div>
                  <p className="font-bold text-[#0d47a1] text-sm">{t('العنوان', 'Address', 'Adresse')}</p>
                  <p className="text-sm text-gray-600 leading-relaxed mt-1">{pick(info.address)}</p>
                </div>
              </div>
            </Card>
          )}

          {info.email && (
            <Card className="p-5" data-testid="contact-info-email">
              <div className="flex items-start gap-3">
                <span className="w-10 h-10 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center flex-shrink-0">
                  <Mail size={18} />
                </span>
                <div>
                  <p className="font-bold text-[#0d47a1] text-sm">{t('البريد الإلكتروني', 'Email', 'Email')}</p>
                  <a href={`mailto:${info.email}`} className="text-sm text-[#1565c0] hover:underline mt-1 block break-all">
                    {info.email}
                  </a>
                </div>
              </div>
            </Card>
          )}

          {info.phone && (
            <Card className="p-5" data-testid="contact-info-phone">
              <div className="flex items-start gap-3">
                <span className="w-10 h-10 rounded-xl bg-amber-50 text-amber-600 flex items-center justify-center flex-shrink-0">
                  <Phone size={18} />
                </span>
                <div>
                  <p className="font-bold text-[#0d47a1] text-sm">{t('رقم الهاتف', 'Phone', 'Téléphone')}</p>
                  <a href={`tel:${info.phone}`} dir="ltr" className="text-sm text-[#1565c0] hover:underline mt-1 block">
                    {info.phone}
                  </a>
                </div>
              </div>
            </Card>
          )}

          {info.website_url && (
            <Card className="p-5" data-testid="contact-info-website">
              <div className="flex items-start gap-3">
                <span className="w-10 h-10 rounded-xl bg-cyan-50 text-cyan-600 flex items-center justify-center flex-shrink-0">
                  <Globe size={18} />
                </span>
                <div>
                  <p className="font-bold text-[#0d47a1] text-sm">{t('الموقع الإلكتروني', 'Website', 'Site web')}</p>
                  <a href={info.website_url} target="_blank" rel="noreferrer" className="text-sm text-[#1565c0] hover:underline mt-1 block">
                    {info.website_display || info.website_url}
                  </a>
                </div>
              </div>
            </Card>
          )}

          {pick(info.hours) && (
            <Card className="p-5" data-testid="contact-info-hours">
              <div className="flex items-start gap-3">
                <span className="w-10 h-10 rounded-xl bg-purple-50 text-purple-600 flex items-center justify-center flex-shrink-0">
                  <Clock size={18} />
                </span>
                <div>
                  <p className="font-bold text-[#0d47a1] text-sm">{t('ساعات العمل', 'Working Hours', 'Horaires')}</p>
                  <p className="text-sm text-gray-600 mt-1">{pick(info.hours)}</p>
                </div>
              </div>
            </Card>
          )}
        </div>

        {/* Form column */}
        <div className="md:col-span-2">
          <Card className="p-6 md:p-8">
            {submitted ? (
              <div className="text-center py-6" data-testid="contact-success-block">
                <div className="mx-auto w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mb-4">
                  <CheckCircle2 size={32} className="text-emerald-600" />
                </div>
                <h2 className="text-xl font-extrabold text-[#0d47a1]">
                  {t('شكراً لتواصلك معنا', 'Thanks for reaching out!', 'Merci !')}
                </h2>
                <p className="text-sm text-gray-600 mt-2">
                  {t(
                    'تم استلام رسالتك وسنرد عليك في أقرب وقت ممكن.',
                    "We received your message and will reply as soon as possible.",
                    'Nous avons reçu votre message.'
                  )}
                </p>
                <Button
                  type="button"
                  onClick={() => { setSubmitted(false); setForm({ ...form, subject: '', message: '' }); }}
                  className="mt-5 bg-[#1e88e5] hover:bg-[#1976d2]"
                  data-testid="contact-send-another"
                >
                  {t('إرسال رسالة أخرى', 'Send another message', 'Envoyer un autre message')}
                </Button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4" data-testid="contact-form">
                <h2 className="text-lg md:text-xl font-extrabold text-[#0d47a1] mb-1">
                  {t('أرسل لنا رسالة', 'Send us a message', 'Envoyez-nous un message')}
                </h2>
                <p className="text-sm text-gray-500">
                  {t('املأ النموذج وسنقوم بالرد عليك بأسرع وقت.', "Fill in the form and we'll get back to you shortly.", '')}
                </p>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <Label>{t('الاسم الكامل', 'Full Name', 'Nom complet')} *</Label>
                    <Input value={form.full_name} onChange={(e) => setField('full_name', e.target.value)}
                      required data-testid="contact-name" placeholder={t('اسمك الكامل', 'Your full name', 'Votre nom')} />
                  </div>
                  <div>
                    <Label>{t('البريد الإلكتروني', 'Email', 'Email')} *</Label>
                    <Input type="email" value={form.email} onChange={(e) => setField('email', e.target.value)}
                      required data-testid="contact-email" placeholder="you@example.com" />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <Label>{t('رقم الهاتف', 'Phone', 'Téléphone')}</Label>
                    <Input value={form.phone} onChange={(e) => setField('phone', e.target.value)}
                      data-testid="contact-phone" placeholder="+961…" />
                  </div>
                  <div>
                    <Label>{t('الموضوع', 'Subject', 'Objet')}</Label>
                    <Input value={form.subject} onChange={(e) => setField('subject', e.target.value)}
                      data-testid="contact-subject" placeholder={t('باختصار، ما الموضوع؟', 'In short, what is it about?', '')} />
                  </div>
                </div>

                <div>
                  <Label>{t('رسالتك', 'Your Message', 'Votre message')} *</Label>
                  <Textarea value={form.message} onChange={(e) => setField('message', e.target.value)}
                    rows={6} required data-testid="contact-message"
                    placeholder={t('اكتب رسالتك هنا…', 'Write your message here…', 'Écrivez ici…')} />
                </div>

                <div className="flex justify-end pt-2">
                  <Button type="submit" disabled={sending}
                    className="bg-[#1e88e5] hover:bg-[#1976d2]" data-testid="contact-submit">
                    <Send size={16} className={isRTL ? 'ml-2' : 'mr-2'} />
                    {sending ? t('جارٍ الإرسال…', 'Sending…', 'Envoi…') : t('إرسال', 'Send Message', 'Envoyer')}
                  </Button>
                </div>
              </form>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
};

export default ContactPage;
